import { Alert, Checkbox, Chip, InputAdornment, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbCategory } from "react-icons/tb";

import { CommonLabel } from "@/components/CommonInput";
import CommonSelector from "@/components/CommonSelector";
import UserTooltipList from "@/components/UserTooltipList";
import { userTypes } from "@/constants/userTypes";
import { RenderUserTypeCellTooltip } from "@/pages/users/components/table/components/UserTypeColumn";
import { User, UserType } from "@/types/user.types";

import { CreateNotificationFormType } from "./CreateNotificationForm";

const validUserTypes = userTypes.filter((item) => !item.hideOnCreateNotification).map((item) => item.value);

function CreateNotificationRecipientSelector({ users }: { users: User[] }) {
    const { setValue } = useFormContext();
    const selectedItems: UserType[] =
        useWatch({ name: "recipients.permissionGroupIds" as unknown as readonly string[] }) || [];
    const { t } = useTranslation();

    const groupedUsers = useMemo<Record<UserType, User[]>>(
        () =>
            users.reduce(
                (prev, user) => {
                    const { userType } = user;
                    if (prev[userType]) prev[userType].push(user);
                    else prev[userType] = [user];

                    return prev;
                },
                {
                    [UserType.DRIVER]: [],
                    [UserType.MANAGER]: [],
                    [UserType.ADMIN]: [],
                    [UserType.WELCOME_TEAM_OFFICER]: [],
                    [UserType.HR]: [],
                    [UserType.KLINIK]: [],
                } as Record<UserType, User[]>
            ),
        [users]
    );
    const handleDelete = useCallback(
        (userType: UserType) => {
            setValue(
                "recipients.permissionGroupIds",
                selectedItems.filter((item) => item !== userType)
            );
        },
        [selectedItems, setValue]
    );

    return (
        <>
            <div className="col-span-full">
                <Alert severity="info" color="info">
                    Bildirim göndermek istediğiniz yetki gruplarını seçiniz.
                </Alert>
            </div>
            <span className="text-gray-500 col-span-full text-sm pl-2">
                {selectedItems.length > 0 ? (
                    <div className="flex flex-wrap gap-2 justify-start">
                        {selectedItems.map((option) => {
                            const userType = userTypes.find((item) => item.value === option);
                            return (
                                userType && (
                                    <Chip
                                        key={option}
                                        label={<RenderUserTypeCellTooltip status={userType} />}
                                        variant="outlined"
                                        sx={{
                                            maxWidth: "fit-content",
                                            borderColor: "transparent !important",
                                        }}
                                        onDelete={() => handleDelete(option)}
                                    />
                                )
                            );
                        })}
                    </div>
                ) : (
                    "Lütfen En az 1 yetki grubu seçiniz."
                )}
            </span>

            <CommonLabel className="col-span-full" label={t("permissionGroups")}>
                <CommonSelector<CreateNotificationFormType, UserType>
                    icon={TbCategory}
                    name="recipients.permissionGroupIds"
                    options={validUserTypes}
                    getOptionLabel={(val) => {
                        return t(val ? "userTypeLabels." + val : "");
                    }}
                    disableCloseOnSelect
                    placeholder="permissionGroups"
                    multiple
                    startAdornment={
                        <Tooltip title="Seçilen yetki grup sayısı">
                            <InputAdornment
                                position="end"
                                className="!-translate-x-[6px] cursor-help !font-semibold !-translate-y-[12px] !w-12 !h-full !flex !items-center !bg-gray-100 !justify-center"
                            >
                                {selectedItems.length}
                            </InputAdornment>
                        </Tooltip>
                    }
                    renderOption={({ option, selected }) => {
                        const item = userTypes.find((item) => item.value === option);
                        if (!item) return null;
                        return (
                            item && (
                                <>
                                    <div className="flex justify-between items-center w-full ">
                                        <Checkbox
                                            className=" mr-2"
                                            color="primary"
                                            size="small"
                                            checked={!!selected}
                                            value={option}
                                            onChange={() => void 0}
                                        />
                                        <RenderUserTypeCellTooltip status={item} />
                                        <UserTooltipList
                                            item={item}
                                            users={groupedUsers[item.value]}
                                            description={`Bu kullanıcı grubunda ${groupedUsers[item.value]?.length || 0} kullanıcı bulunmaktadır.`}
                                        >
                                            <span className=" ml-auto grow-0  text-gray-500 shrink-0 text-right">
                                                {groupedUsers[item.value]?.length || 0}
                                            </span>
                                        </UserTooltipList>
                                    </div>
                                </>
                            )
                        );
                    }}
                />
            </CommonLabel>
        </>
    );
}

export default CreateNotificationRecipientSelector;
